import type { TranscriptBlock } from '../../transport/api'
import { parseInbound } from '../inbound'
import { rowWords, tabFacts, tabNotice } from '../browserActivity'
import type { TurnItem } from '../turns'

/**
 * SEARCH THE LOADED CLEAN VIEW — owner, 2026-09-11: 當使用者按下這個搜尋按鈕的時候整個
 * text input 會變為輸入搜尋 keyword 的功能, 一旦使用者輸入這個 keyword 就會對照 cleanview
 * 裡面所有快取的 block 來做比對搜尋.
 *
 * Pure: blocks in, hits out. The corpus is exactly what the view holds — the 1000-block
 * window useTranscriptWindow opened with plus whatever scroll-back added — never a
 * request to the edge; the whole point is that the answer is already on the device.
 *
 * A plain case-insensitive substring. No tokenising, no fuzziness: a person searching a
 * transcript for `pump fix` wants the lines that contain those characters, and a
 * cleverer match would hide some of them.
 *
 * THE CONVERSATION ONLY — owner, 2026-09-12: 搜尋去找對話內容就好, 不要去找 result / Edit /
 * Bash 這種的 Block. What was said, by the person or the agent; never a tool call, its
 * result, or the agent's private reasoning. A file path in a Bash line is not what
 * anyone means by "find where we talked about that".
 */

/** The blocks a search reads: speech, in either direction. */
const spoken = (b: TranscriptBlock) => b.kind === 'user' || b.kind === 'assistant'

/**
 * What a person could be looking for in a spoken block: the text the view shows. For
 * a message that ARRIVED — from another agent, a chat, a project room — that is its
 * sender's words: the reply instructions under every one are drawn nowhere, and
 * searching them made "reply" or "readme" find every message another agent sent
 * (the edge's search reads the same body — transcriptSearch.ts inboundOf there).
 */
function searchableText(b: TranscriptBlock): string {
  if (typeof b.text !== 'string') return ''
  return (b.kind === 'user' && parseInbound(b.text)?.body) || b.text
}

/**
 * A browser tab's notice is drawn as two lines the notice does not contain — its tab's
 * title, and what happened in the reader's words — and its own lines ("Look: avengents
 * browser snapshot …") are drawn nowhere. So it is searched as it is DRAWN: "snapshot"
 * used to outline every notice in the conversation with nothing on the row that matched,
 * and the title the row shows could not be found on it. Not kept by identity like the
 * rest: the title arrives later than the block, when the agent first looks at the tab.
 */
function noticeText(b: TranscriptBlock, blocks: readonly TranscriptBlock[]): string | null {
  const act = tabNotice(b)
  if (!act) return null
  const { title, under } = rowWords(act, tabFacts(blocks).get(b))
  return `browser activity\n${title}\n${under}`.toLowerCase()
}

// Lower-casing 1000 blocks per keystroke is the one cost here; a block's text never
// changes once it exists, so the lowered copy is kept by identity and computed once.
const lowered = new WeakMap<TranscriptBlock, string>()
function lower(b: TranscriptBlock): string {
  let s = lowered.get(b)
  if (s === undefined) {
    s = searchableText(b).toLowerCase()
    lowered.set(b, s)
  }
  return s
}

/** The SPOKEN blocks whose text contains the query, case-insensitively. A blank query matches nothing. */
export function findHits(blocks: readonly TranscriptBlock[], query: string): Set<TranscriptBlock> {
  const q = query.trim().toLowerCase()
  const out = new Set<TranscriptBlock>()
  if (!q) return out
  for (const b of blocks) {
    if (!spoken(b)) continue
    if ((noticeText(b, blocks) ?? lower(b)).includes(q)) out.add(b)
  }
  return out
}

/**
 * Indices of the RENDERED items — a said message or a whole turn — that hold at least
 * one hit, in transcript order. The reader steps through these, not through blocks: a
 * turn is one thing on the screen, and its hits are marked inside it once it opens.
 */
export function hitItems(items: readonly TurnItem[], hits: ReadonlySet<TranscriptBlock>): number[] {
  const out: number[] = []
  if (hits.size === 0) return out
  items.forEach((it, i) => {
    const has = it.kind === 'said' ? hits.has(it.block) : it.blocks.some((b) => hits.has(b))
    if (has) out.push(i)
  })
  return out
}
